import { APPROVAL_MODE_COPY, ENTITY_APPROVAL_MODES, type EntityApprovalMode } from "@/services/api/entityApi";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 1 — "What may act without me" (D-206-010 row 1, kept-editable).
 *
 * **Who does what:** the person chooses how much Raisy may do on its own; Raisy then either waits
 * for his word, does the everyday part and waits for the rest, does it and reports, or acts only
 * where it is confident.
 * **What he sees:** the sentence his setting currently means, and a picker holding the four choices.
 * **What he feels:** *"I decide, bots do the toil"* — Sam's W3 want, which is exactly why this is row
 * one and why it is editable rather than a statement of fact.
 *
 * THE VALUE IS THE SENTENCE, NOT THE CONSTANT. `SEMI_AUTO` is the BE's word for the middle setting;
 * showing it to a person would be showing him our schema (RULES_NAMING §3, §6 — one concept, one
 * name, and the name the reader meets is the canonical one). The BE vocabulary stays in the picker's
 * `value` attributes; the plain words live in `APPROVAL_MODE_COPY` (entityApi.ts) so that this
 * section and the Process Manager's per-row badge (T0203) cannot drift into two vocabularies.
 */

export const ActWithoutMeSection = ({ settings }: { settings: UserSettings }) => {
  const { approvalMode, saveApprovalMode, approvalSave, approvalSaveError } = settings;

  const value =
    approvalMode.state === "loading" ? (
      <span className="text-muted-foreground">Reading your setting…</span>
    ) : approvalMode.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {approvalMode.reason}. Your setting is unchanged.
      </span>
    ) : (
      <span>
        {APPROVAL_MODE_COPY[approvalMode.value].label}
        <span className="ml-2 font-normal text-muted-foreground">
          {APPROVAL_MODE_COPY[approvalMode.value].meaning}
        </span>
      </span>
    );

  return (
    <SettingsSection
      title="What may act without me"
      hint="How much Raisy does on its own, and where it stops to ask you."
      value={value}
      saveState={approvalSave}
      saveError={approvalSaveError}
      control={
        approvalMode.state === "ready" ? (
          <select
            aria-label="What may act without me"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={approvalMode.value}
            disabled={approvalSave === "saving"}
            onChange={(e) => void saveApprovalMode(e.target.value as EntityApprovalMode)}
          >
            {ENTITY_APPROVAL_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {APPROVAL_MODE_COPY[mode].label}
              </option>
            ))}
          </select>
        ) : undefined
      }
    />
  );
};
